import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, FileDown, Share2, RefreshCw, Pencil, Eye, ClipboardList } from "lucide-react";

import iiApi from "@/lib/incomingInspectionApi";
import { formatApiError } from "@/lib/api";
import { fmtLimit } from "@/lib/incomingInspectionTolerance";
import { exportReportToPDF } from "@/lib/incomingInspectionPdf";
import ControlChart from "@/components/incoming-inspection/ControlChart";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const QC_KEYS = ["visual", "fitment", "surface_finish", "thread1", "thread2"];
const QC_LABELS = { visual: "Visual", fitment: "Fitment", surface_finish: "Surface Finish", thread1: "Thread 1", thread2: "Thread 2" };

function ncrKey(source, dimIndex) { return `${source}:${dimIndex || ""}`; }
function fmt(v, d = 3) { return v === null || v === undefined || v === "" || isNaN(v) ? "—" : Number(v).toFixed(d); }

function StatusBadge({ status }) {
  if (status === "ACCEPTED") return <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700">Accepted</span>;
  if (status === "NC") return <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-red-50 text-red-700">NC</span>;
  return <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-slate-100 text-slate-500">—</span>;
}

function NcrCell({ reportId, ncrMap, source, dimIndex }) {
  const entry = ncrMap[ncrKey(source, dimIndex)];
  const qs = new URLSearchParams({ report_id: reportId, source, back: "/quality/incoming-inspection/history" });
  if (dimIndex) qs.set("dimension_index", String(dimIndex));
  const href = `/quality/incoming-inspection/ncr?${qs.toString()}`;
  if (!entry) {
    return <a href={href} target="_blank" rel="noreferrer" className="inline-flex items-center text-[11px] font-semibold px-2 py-1 rounded-md border border-slate-300 text-slate-700 hover:bg-slate-50">Raise NCR</a>;
  }
  const isClosed = entry.status === "CLOSED";
  const cls = !entry.is_complete ? "bg-slate-100 text-slate-500" : isClosed ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-700";
  const label = !entry.is_complete ? `${entry.ncr_no} · Draft` : `${entry.ncr_no} · ${isClosed ? "Closed" : "Open"}`;
  return <a href={href} target="_blank" rel="noreferrer"><span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${cls}`}>{label}</span></a>;
}

function StatGrid({ r, dimCount }) {
  const tiles = [
    { label: "Grand Avg (X̿)", value: fmt(r.x_double_bar) },
    { label: "Avg Range (R̄)", value: fmt(r.r_bar) },
    { label: "UCL (X̄)", value: fmt(r.ucl_x) },
    { label: "LCL (X̄)", value: fmt(r.lcl_x) },
    { label: "Cp", value: fmt(r.cp), cls: r.cp == null ? "" : r.cp >= 1.33 ? "good" : "warn" },
    { label: "Cpk", value: fmt(r.cpk), cls: r.cpk == null ? "" : r.cpk >= 1.33 ? "good" : "warn" },
    { label: "UCL (R)", value: fmt(r.ucl_r) },
    { label: "LCL (R)", value: fmt(r.lcl_r) },
    { label: "Dimensions", value: String(dimCount) },
    { label: "Sample Size (n)", value: String(r.subgroup_size) },
    { label: "Out-of-Control Pts", value: String(r.out_of_control_count), cls: r.out_of_control_count > 0 ? "warn" : "good" },
    { label: "Process Status", value: r.out_of_control_count > 0 ? "Unstable" : "In Control", cls: r.out_of_control_count > 0 ? "warn" : "good" },
  ];
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
      {tiles.map((t) => (
        <div key={t.label} className={`rounded-lg border p-3 ${t.cls === "warn" ? "border-red-200 bg-red-50" : t.cls === "good" ? "border-emerald-200 bg-emerald-50" : "border-slate-200 bg-slate-50"}`}>
          <div className="text-[10px] uppercase tracking-wider text-slate-500">{t.label}</div>
          <div className={`text-lg font-bold mt-0.5 ${t.cls === "warn" ? "text-red-700" : t.cls === "good" ? "text-emerald-700" : "text-slate-800"}`}>{t.value}</div>
        </div>
      ))}
    </div>
  );
}

function ReportDetail({ id }) {
  const nav = useNavigate();
  const [report, setReport] = useState(null);
  const [ncrMap, setNcrMap] = useState({});
  const [downloading, setDownloading] = useState(false);

  const load = async () => {
    try {
      const { report: r, ncrs } = await iiApi.getReport(id);
      setReport(r);
      const map = {};
      (ncrs || []).forEach((n) => { map[ncrKey(n.source, n.dimension_index)] = n; });
      setNcrMap(map);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || err.message);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const refreshNcrs = async () => {
    try {
      const res = await iiApi.listNcrs({ report_id: id });
      const map = {};
      (res.ncrs || []).forEach((n) => { map[ncrKey(n.source, n.dimension_index)] = n; });
      setNcrMap(map);
      toast.success("NCR statuses refreshed.");
    } catch (err) {
      toast.error("Could not refresh NCR statuses: " + (formatApiError(err?.response?.data?.detail) || err.message));
    }
  };

  if (!report) return <div className="text-sm text-slate-500 py-10 text-center">Loading report…</div>;
  const r = report;
  const rows = r.rows || [];

  const handleDownloadPdf = async () => {
    setDownloading(true);
    try {
      const rowsForPdf = rows.map((row, i) => {
        const entry = ncrMap[ncrKey("dimension", i + 1)];
        return entry ? { ...row, ncr: { ncr_no: entry.ncr_no, status: entry.status, is_complete: entry.is_complete, detail: entry } } : row;
      });
      const qc = {};
      QC_KEYS.forEach((key) => {
        const base = { result: r[`${key}_result`], remarks: r[`${key}_remarks`] };
        const entry = ncrMap[ncrKey(key, null)];
        qc[key] = entry ? { ...base, ncr: { ncr_no: entry.ncr_no, status: entry.status, is_complete: entry.is_complete, detail: entry } } : base;
      });
      await exportReportToPDF({
        meta: {
          title: r.title, process_name: r.process_name, part_number: r.part_number, part_description: r.part_description,
          shift_code: r.shift_code, drawing_revision: r.drawing_revision, operator_name: r.operator_name,
          unit_of_measure: r.unit_of_measure, subgroup_size: r.subgroup_size, lsl: r.lsl, notes: r.notes,
          lot_qty: r.lot_qty, supplier_name: r.supplier_name, created_at: new Date(r.created_at).toLocaleString(),
        },
        stats: { xDoubleBar: r.x_double_bar, rBar: r.r_bar, uclX: r.ucl_x, lclX: r.lcl_x, uclR: r.ucl_r, lclR: r.lcl_r, cp: r.cp, cpk: r.cpk, outOfControlCount: r.out_of_control_count },
        rows: rowsForPdf, qc, images: r.images || [],
      });
      toast.success("PDF downloaded.");
    } catch (err) {
      toast.error("Could not generate the PDF. Please try again.");
    } finally {
      setDownloading(false);
    }
  };

  const handleShare = () => {
    const f = (v) => (v === null || v === undefined ? "—" : Number(v).toFixed(3));
    const text = `*SPC Report: ${r.title}*
Process: ${r.process_name || "—"}
Part No: ${r.part_number || "—"}

Grand Avg (X̿): ${f(r.x_double_bar)}
Avg Range (R̄): ${f(r.r_bar)}
UCL/LCL (X̄): ${f(r.ucl_x)} / ${f(r.lcl_x)}
Cp: ${f(r.cp)}  Cpk: ${f(r.cpk)}
Status: ${r.out_of_control_count > 0 ? "Unstable (" + r.out_of_control_count + " out-of-control pts)" : "In Control"}

Generated with Incoming/In-Process Inspection — Canares Automation ERP`;
    window.open("https://wa.me/?text=" + encodeURIComponent(text), "_blank");
  };

  return (
    <div className="space-y-6">
      <Button variant="outline" size="sm" onClick={() => nav("/quality/incoming-inspection/history")}>
        <ArrowLeft className="w-3.5 h-3.5 mr-1.5" />Back to History
      </Button>

      <section className="bg-white border border-slate-200 rounded-lg p-6">
        <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
          <div>
            <h2 className="text-xl font-extrabold text-slate-900" style={{ fontFamily: "Manrope" }}>{r.title}</h2>
            <p className="text-xs text-slate-500 mt-1">
              {[r.process_name, r.part_number ? `Part: ${r.part_number}` : "", new Date(r.created_at).toLocaleString()].filter(Boolean).join(" · ")}
            </p>
          </div>
          <Link to={`/quality/incoming-inspection?edit=${r.id}`}><Button variant="outline" size="sm"><Pencil className="w-3.5 h-3.5 mr-1.5" />Edit</Button></Link>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-3"><div className="text-[10px] uppercase tracking-wider text-slate-500">Operator</div><div className="text-sm font-semibold text-slate-800 mt-0.5">{r.operator_name || "—"}</div></div>
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-3"><div className="text-[10px] uppercase tracking-wider text-slate-500">Unit</div><div className="text-sm font-semibold text-slate-800 mt-0.5">{r.unit_of_measure || "—"}</div></div>
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-3"><div className="text-[10px] uppercase tracking-wider text-slate-500">LSL</div><div className="text-sm font-semibold text-slate-800 mt-0.5">{r.lsl ?? "—"}</div></div>
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-3"><div className="text-[10px] uppercase tracking-wider text-slate-500">Dimension Size</div><div className="text-sm font-semibold text-slate-800 mt-0.5">{r.subgroup_size}</div></div>
        </div>

        {Array.isArray(r.images) && r.images.length > 0 && (
          <div className="mb-6 text-center">
            <h3 className="text-sm font-semibold text-slate-700 mb-2">Process / Part Images</h3>
            <div className="flex flex-wrap gap-3 justify-center">
              {r.images.map((uri, i) => <img key={i} src={uri} alt={`Process ${i + 1}`} className="max-w-[220px] max-h-[220px] rounded-md border border-slate-200 object-cover" />)}
            </div>
          </div>
        )}

        <StatGrid r={r} dimCount={rows.length} />
        <ControlChart title="X̄ Chart (Dimension Averages)" points={rows.map((s) => Number(s.mean))} ucl={Number(r.ucl_x)} lcl={Number(r.lcl_x)} cl={Number(r.x_double_bar)} flags={rows.map((s) => !!s.is_out_of_control)} />
        <ControlChart title="R Chart (Dimension Ranges)" points={rows.map((s) => Number(s.range))} ucl={Number(r.ucl_r)} lcl={Number(r.lcl_r)} cl={Number(r.r_bar)} flags={rows.map((s) => !!s.is_out_of_control)} />

        <div className="mt-6">
          <h3 className="text-sm font-bold text-slate-800 mb-2">Dimension Data Entry</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-xs border-collapse">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
                  <th className="py-2 pr-2 w-8">#</th><th className="py-2 pr-2">Dimension</th><th className="py-2 pr-2">Tolerance</th>
                  <th className="py-2 pr-2 w-16">Min</th><th className="py-2 pr-2 w-16">Max</th><th className="py-2 pr-2">Sample Readings</th>
                  <th className="py-2 pr-2 w-20">Status</th><th className="py-2 pr-2 w-36">NCR Report</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 && <tr><td colSpan={8} className="py-6 text-center text-slate-400">No dimensions recorded.</td></tr>}
                {rows.map((row, i) => (
                  <tr key={i} className="border-b border-slate-100">
                    <td className="py-1.5 pr-2 text-slate-500 font-semibold">#{i + 1}</td>
                    <td className="py-1.5 pr-2">{row.dimension || "—"}</td>
                    <td className="py-1.5 pr-2">{row.tolerance || "—"}</td>
                    <td className="py-1.5 pr-2">{row.min_limit != null ? fmtLimit(row.min_limit) : "—"}</td>
                    <td className="py-1.5 pr-2">{row.max_limit != null ? fmtLimit(row.max_limit) : "—"}</td>
                    <td className="py-1.5 pr-2">{(row.samples || []).join(", ") || "—"}</td>
                    <td className="py-1.5 pr-2"><StatusBadge status={row.status} /></td>
                    <td className="py-1.5 pr-2">{row.status === "NC" ? <NcrCell reportId={r.id} ncrMap={ncrMap} source="dimension" dimIndex={i + 1} /> : <span className="text-slate-300">—</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="mt-6">
          <h3 className="text-sm font-bold text-slate-800 mb-2">Visual, Fitment &amp; Surface Finish</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-xs border-collapse">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
                  <th className="py-2 pr-2 w-32">Check</th><th className="py-2 pr-2">Remarks</th><th className="py-2 pr-2 w-24">Result</th><th className="py-2 pr-2 w-36">NCR Report</th>
                </tr>
              </thead>
              <tbody>
                {QC_KEYS.map((key) => {
                  const result = r[`${key}_result`] || "ACCEPTED";
                  const remarks = r[`${key}_remarks`];
                  return (
                    <tr key={key} className="border-b border-slate-100">
                      <td className="py-2 pr-2 font-semibold text-slate-700">{QC_LABELS[key]}</td>
                      <td className="py-2 pr-2">{remarks || "—"}</td>
                      <td className="py-2 pr-2"><StatusBadge status={result === "NC" ? "NC" : "ACCEPTED"} /></td>
                      <td className="py-2 pr-2">{result === "NC" ? <NcrCell reportId={r.id} ncrMap={ncrMap} source={key} dimIndex={null} /> : <span className="text-slate-300">—</span>}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        <div className="flex flex-wrap gap-2 mt-6">
          <Button className="bg-blue-600 hover:bg-blue-700" onClick={handleDownloadPdf} disabled={downloading}><FileDown className="w-4 h-4 mr-1.5" />Download PDF</Button>
          <Button variant="outline" onClick={handleShare}><Share2 className="w-4 h-4 mr-1.5" />Share to WhatsApp</Button>
          <Button variant="outline" onClick={refreshNcrs}><RefreshCw className="w-4 h-4 mr-1.5" />Refresh NCR</Button>
        </div>
      </section>
    </div>
  );
}

function ReportList() {
  const nav = useNavigate();
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [reports, setReports] = useState(null);

  const load = async (params) => {
    setReports(null);
    try {
      const res = await iiApi.listReports(params);
      setReports(res.reports || []);
    } catch (err) {
      setReports([]);
      toast.error(formatApiError(err?.response?.data?.detail) || err.message);
    }
  };
  useEffect(() => { load({}); /* eslint-disable-next-line */ }, []);

  const applyFilter = () => load({ from: from || undefined, to: to || undefined });
  const clearFilter = () => { setFrom(""); setTo(""); load({}); };

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.18em] text-slate-500">Quality</div>
          <h1 className="text-3xl sm:text-4xl tracking-tight font-extrabold text-slate-900" style={{ fontFamily: "Manrope" }}>Report History</h1>
          <p className="text-sm text-slate-500 mt-1">Incoming/In-Process Inspection — all saved SPC reports, most recent first.</p>
        </div>
        <Link to="/quality/incoming-inspection"><Button className="bg-blue-600 hover:bg-blue-700">New Inspection</Button></Link>
      </div>

      <section className="bg-white border border-slate-200 rounded-lg p-4 flex flex-wrap items-end gap-3">
        <div><Label>From Date</Label><Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} /></div>
        <div><Label>To Date</Label><Input type="date" value={to} onChange={(e) => setTo(e.target.value)} /></div>
        <Button onClick={applyFilter}>Apply Filter</Button>
        <Button variant="outline" onClick={clearFilter}>Clear</Button>
      </section>

      <section className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-100 text-sm font-semibold text-slate-700">Saved Reports</div>
        {reports === null && <div className="py-10 text-center text-sm text-slate-500">Loading…</div>}
        {reports && reports.length === 0 && (
          <div className="py-14 text-center text-slate-400">
            <ClipboardList className="w-10 h-10 mx-auto mb-2" />
            <p className="text-sm">No reports found for this date range.</p>
          </div>
        )}
        {reports && reports.length > 0 && (
          <div className="divide-y divide-slate-100">
            {reports.map((r) => {
              const ooc = r.out_of_control_count || 0;
              return (
                <div key={r.id} className="px-4 py-3 flex items-center justify-between gap-3 flex-wrap hover:bg-slate-50">
                  <div>
                    <h3 className="text-sm font-semibold text-slate-800">
                      {r.title}
                      {r.stage === "final_inspection" && (
                        <span className="ml-2 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-orange-50 text-orange-700 align-middle">Final Inspection</span>
                      )}
                    </h3>
                    <div className="text-xs text-slate-500 mt-0.5 flex flex-wrap gap-x-3">
                      <span>{r.process_name || "—"}</span>
                      <span>Part: {r.part_number || "—"}</span>
                      {r.job_code && <span>Job: {r.job_code}</span>}
                      <span>{new Date(r.created_at).toLocaleString()}</span>
                      <span>Cp {fmt(r.cp)} / Cpk {fmt(r.cpk)}</span>
                      {r.created_by_name && <span>By: {r.created_by_name}</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {r.has_nc
                      ? <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-red-50 text-red-700">{ooc} Out-of-Control</span>
                      : <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700">In Control</span>}
                    <Link to={`/quality/incoming-inspection?edit=${r.id}`}><Button variant="outline" size="sm"><Pencil className="w-3.5 h-3.5 mr-1" />Edit</Button></Link>
                    <Button size="sm" className="bg-blue-600 hover:bg-blue-700" onClick={() => nav(`/quality/incoming-inspection/history/${r.id}`)}><Eye className="w-3.5 h-3.5 mr-1" />View</Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}

export default function IncomingInspectionHistory() {
  const { id } = useParams();
  const view = useMemo(() => (id ? "detail" : "list"), [id]);
  return view === "detail" ? <ReportDetail id={id} /> : <ReportList />;
}
